import type { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import type { Item } from "@coworker/apprestructured/src/shared/types/item";
import type {
  MediaAboutFieldKey,
  MediaAboutFieldValues,
} from "../components/MediaAboutFields/MediaAboutFields";
import type { LaunchMediaArticle, LaunchMediaAttribute } from "../types/launch";

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z]/g, "");

// Backend attribute names (normalized) mapped to MediaAboutFields keys.
const ATTRIBUTE_ALIASES: Record<string, MediaAboutFieldKey> = {
  focus: "focus",
  livingsituation: "livingSituation",
  stylegroup: "styleGroup",
  longtermpriority: "longTermPriority",
  hfltp: "longTermPriority",
  pricing: "pricing",
  pricelevel: "pricing",
  location: "location",
  rangedescription: "rangeDescription",
  productrange: "productRange",
  firstgrouping: "firstGrouping",
  secondgrouping: "secondGrouping",
  thirdgrouping: "thirdGrouping",
  merchandisingpa: "merchandisingPa",
  pa: "merchandisingPa",
  colorandncs: "colorAndNcs",
  colourandncs: "colorAndNcs",
};

export const mapAttributesToAboutFieldValues = (
  attributes: LaunchMediaAttribute[] = []
): MediaAboutFieldValues =>
  attributes.reduce<MediaAboutFieldValues>((values, { attribute, value }) => {
    const key = ATTRIBUTE_ALIASES[normalize(attribute)];
    if (key && value) values[key] = value;
    return values;
  }, {});

export type MediaDetailProduct = {
  item: Item;
  article: Article;
};

const UNKNOWN = "UNKNOWN";
const known = (value?: string | null) =>
  value && value !== UNKNOWN ? value : undefined;

const formatItemNo = (no: string) =>
  `${no.slice(0, 3)}.${no.slice(3, 6)}.${no.slice(6)}`;

// API product_article_type is "ARTICLE"; the app's item/article types use "ART"/"SPR".
const toItemType = (type?: string | null) =>
  !type || type === "ARTICLE" ? "ART" : type;

const mapLaunchArticleToArticle = (
  launchArticle: LaunchMediaArticle,
  mediaId: string
): Article => ({
  id: launchArticle.id,
  active: launchArticle.active === "false" ? "INACTIVE" : "ACTIVE",
  itemName: launchArticle.item_name ?? "",
  itemType: known(launchArticle.item_type) ?? "",
  itemColor: known(launchArticle.item_color) ?? "",
  imageSmall: launchArticle.image_small ?? "",
  nbrArticles: launchArticle.nbr_articles ?? 1,
  hfb: known(launchArticle.hfb) ?? "",
  productArticleType: toItemType(launchArticle.product_article_type),
  productArticleId: launchArticle.product_article_id,
  mediaId: launchArticle.media_id ?? mediaId,
  supplierNumber: launchArticle.supplier_number ?? undefined,
  groupId: launchArticle.group_id ?? undefined,
  dateStamp: launchArticle.date_stamp ?? undefined,
  createdAt: launchArticle.created_at ?? "",
  parentArticleCombinationNumber:
    launchArticle.parent_article_combination_number ?? undefined,
  priceLevel: launchArticle.price_level ?? undefined,
  styleGroup: launchArticle.style_group ?? undefined,
});

export const mapArticlesToProducts = (
  launchArticles: LaunchMediaArticle[],
  items: Item[],
  mediaId: string
): MediaDetailProduct[] =>
  launchArticles.map((launchArticle) => {
    const article = mapLaunchArticleToArticle(launchArticle, mediaId);
    const no = article.productArticleId;
    const item: Item = items.find((candidate) => candidate.no === no) ?? {
      no,
      type: article.productArticleType,
      name: article.itemName || no,
      noFormatted: formatItemNo(no),
      description: "",
      productTypeName: "",
      paNo: "",
      hfbNo: article.hfb,
      source: "GLOBAL",
      smallImageUrl: article.imageSmall,
    };

    return {
      item: {
        ...item,
        smallImageUrl: item.smallImageUrl || article.imageSmall,
      },
      article: {
        ...article,
        itemName: article.itemName || item.name,
        imageSmall: article.imageSmall || item.smallImageUrl || "",
      },
    };
  });
